/**
 * Local Attendance Storage Service
 * Unified storage for both pending and synced attendance records
 * Uses IndexedDB for web and SQLite for native platforms
 */

import { Capacitor } from '@capacitor/core'
import type { LocalAttendanceRecord } from '@/types/attendance'

// Database configuration
const DB_NAME = 'warden_attendance'
const DB_VERSION = 2 // Incremented for schema change
const STORE_NAME = 'attendance_records'
const TABLE_NAME = 'attendance_records'

// Platform detection
const isNative = Capacitor.isNativePlatform()

// IndexedDB instance (web only)
let db: IDBDatabase | null = null

// SQLite plugin (native only)
let SQLite: any = null
let sqliteDbConnection: any = null

// Initialization guard to prevent concurrent initialization
let initializationPromise: Promise<void> | null = null

/**
 * Database initialization error
 */
export class DatabaseInitializationError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'DatabaseInitializationError'
    }
}

/**
 * Database operation error
 */
export class DatabaseOperationError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'DatabaseOperationError'
    }
}

/**
 * Initialize the database based on platform
 * Uses a promise guard to prevent concurrent initialization
 */
export async function initializeDatabase(): Promise<void> {
    // Check if already initialized
    if (isNative && sqliteDbConnection) {
        // Verify SQLite connection is still valid
        try {
            const isOpen = await sqliteDbConnection.isDBOpen()
            if (isOpen.result) {
                return // Already initialized and open
            }
        } catch (error) {
            // Connection invalid, need to reinitialize
            console.log('[LocalAttendanceStorage] Existing SQLite connection invalid, reinitializing')
            sqliteDbConnection = null
        }
    } else if (!isNative && db) {
        return // IndexedDB already initialized
    }

    // If initialization is already in progress, wait for it to complete
    if (initializationPromise) {
        console.log('[LocalAttendanceStorage] Initialization already in progress, waiting...')
        return initializationPromise
    }

    // Start new initialization
    initializationPromise = (async () => {
        try {
            if (isNative) {
                await initializeSQLite()
            } else {
                await initializeIndexedDB()
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown initialization error'
            console.error('[LocalAttendanceStorage] Database initialization failed:', errorMessage)
            // Clear the promise on error so retry is possible
            initializationPromise = null
            throw new DatabaseInitializationError(`Failed to initialize database: ${errorMessage}`)
        }
    })()

    return initializationPromise
}

/**
 * Initialize IndexedDB for web platform
 */
async function initializeIndexedDB(): Promise<void> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION)

        request.onerror = () => {
            reject(new Error('Failed to open IndexedDB'))
        }

        request.onsuccess = () => {
            db = request.result

            db.onerror = event => {
                console.error('[LocalAttendanceStorage] IndexedDB error:', event)
            }

            db.onabort = event => {
                console.error('[LocalAttendanceStorage] IndexedDB transaction aborted:', event)
            }

            resolve()
        }

        request.onupgradeneeded = event => {
            const db = (event.target as IDBOpenDBRequest).result

            // Delete old store if exists
            if (db.objectStoreNames.contains('offline_attendance_records')) {
                db.deleteObjectStore('offline_attendance_records')
            }

            // Create new object store if it doesn't exist
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' })

                // Create indexes for efficient querying
                objectStore.createIndex('workerId', 'workerId', { unique: false })
                objectStore.createIndex('syncStatus', 'syncStatus', { unique: false })
                objectStore.createIndex('checkInTime', 'checkInTime', { unique: false })
                objectStore.createIndex('createdAt', 'createdAt', { unique: false })
                objectStore.createIndex('recordId', 'recordId', { unique: false })
            }
        }
    })
}

/**
 * Get or create SQLite database connection
 */
async function getSQLiteConnection(): Promise<any> {
    if (!SQLite) {
        throw new Error('SQLite not initialized')
    }

    if (sqliteDbConnection) {
        try {
            const isOpen = await sqliteDbConnection.isDBOpen()
            if (isOpen.result) {
                return sqliteDbConnection
            }
        } catch (error) {
            console.log('[LocalAttendanceStorage] Existing connection invalid, creating new one')
            sqliteDbConnection = null
        }
    }

    try {
        sqliteDbConnection = await SQLite.retrieveConnection(DB_NAME, false)
        console.log('[LocalAttendanceStorage] Retrieved existing SQLite connection')
    } catch (error) {
        console.log('[LocalAttendanceStorage] Creating new SQLite connection')
        sqliteDbConnection = await SQLite.createConnection(
            DB_NAME,
            false,
            'no-encryption',
            DB_VERSION,
            false
        )
    }

    const isOpen = await sqliteDbConnection.isDBOpen()
    if (!isOpen.result) {
        await sqliteDbConnection.open()
    }

    return sqliteDbConnection
}

/**
 * Initialize SQLite for native platforms
 */
async function initializeSQLite(): Promise<void> {
    try {
        const { CapacitorSQLite, SQLiteConnection } = await import('@capacitor-community/sqlite')
        SQLite = new SQLiteConnection(CapacitorSQLite)

        const db = await getSQLiteConnection()

        // Create table with new schema
        const createTableSQL = `
            CREATE TABLE IF NOT EXISTS ${TABLE_NAME} (
                id TEXT PRIMARY KEY NOT NULL,
                workerId INTEGER NOT NULL,
                checkInTime TEXT NOT NULL,
                checkOutTime TEXT,
                lat REAL NOT NULL,
                lng REAL NOT NULL,
                qrCode TEXT NOT NULL,
                action TEXT NOT NULL CHECK(action IN ('check-in', 'check-out')),
                syncStatus TEXT NOT NULL CHECK(syncStatus IN ('pending', 'synced')),
                status TEXT NOT NULL CHECK(status IN ('on-time', 'late', 'early-exit', 'forgot-checkout', 'manual-entry')),
                recordId INTEGER,
                createdAt TEXT NOT NULL,
                updatedAt TEXT,
                errorMessage TEXT,
                retryCount INTEGER DEFAULT 0
            );
        `

        await db.execute(createTableSQL)

        // Create indexes
        const createIndexesSQL = [
            `CREATE INDEX IF NOT EXISTS idx_workerId ON ${TABLE_NAME}(workerId);`,
            `CREATE INDEX IF NOT EXISTS idx_syncStatus ON ${TABLE_NAME}(syncStatus);`,
            `CREATE INDEX IF NOT EXISTS idx_checkInTime ON ${TABLE_NAME}(checkInTime);`,
            `CREATE INDEX IF NOT EXISTS idx_createdAt ON ${TABLE_NAME}(createdAt);`,
            `CREATE INDEX IF NOT EXISTS idx_recordId ON ${TABLE_NAME}(recordId);`
        ]

        for (const sql of createIndexesSQL) {
            await db.execute(sql)
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown SQLite error'
        throw new Error(`SQLite initialization failed: ${errorMessage}`)
    }
}

/**
 * Save a record to the database
 */
export async function saveRecord(record: LocalAttendanceRecord): Promise<void> {
    try {
        if (isNative) {
            await saveRecordSQLite(record)
        } else {
            await saveRecordIndexedDB(record)
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown save error'
        console.error('[LocalAttendanceStorage] Failed to save record:', errorMessage)
        throw new DatabaseOperationError(`Failed to save record: ${errorMessage}`)
    }
}

/**
 * Save record to IndexedDB
 */
async function saveRecordIndexedDB(record: LocalAttendanceRecord): Promise<void> {
    return new Promise((resolve, reject) => {
        if (!db) {
            reject(new Error('Database not initialized'))
            return
        }

        const transaction = db.transaction([STORE_NAME], 'readwrite')
        const objectStore = transaction.objectStore(STORE_NAME)
        const request = objectStore.put(record)

        request.onsuccess = () => {
            resolve()
        }

        request.onerror = () => {
            reject(new Error('Failed to save record to IndexedDB'))
        }
    })
}

/**
 * Save record to SQLite
 */
async function saveRecordSQLite(record: LocalAttendanceRecord): Promise<void> {
    const db = await getSQLiteConnection()

    const insertSQL = `
        INSERT OR REPLACE INTO ${TABLE_NAME}
        (id, workerId, checkInTime, checkOutTime, lat, lng, qrCode, action, syncStatus, status, recordId, createdAt, updatedAt, errorMessage, retryCount)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?);
    `

    const values = [
        record.id,
        record.workerId,
        record.checkInTime,
        record.checkOutTime || null,
        record.location.lat,
        record.location.lng,
        record.qrCode,
        record.action,
        record.syncStatus,
        record.status,
        record.recordId || null,
        record.createdAt,
        record.updatedAt || null,
        record.errorMessage || null,
        record.retryCount || 0
    ]

    await db.run(insertSQL, values)
}

/**
 * Get all pending records (syncStatus = 'pending')
 */
export async function getAllPendingRecords(): Promise<LocalAttendanceRecord[]> {
    try {
        if (isNative) {
            return await getAllPendingRecordsSQLite()
        } else {
            return await getAllPendingRecordsIndexedDB()
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown retrieval error'
        console.error('[LocalAttendanceStorage] Failed to get pending records:', errorMessage)
        throw new DatabaseOperationError(`Failed to get pending records: ${errorMessage}`)
    }
}

/**
 * Get pending records from IndexedDB
 */
async function getAllPendingRecordsIndexedDB(): Promise<LocalAttendanceRecord[]> {
    return new Promise((resolve, reject) => {
        if (!db) {
            reject(new Error('Database not initialized'))
            return
        }

        const transaction = db.transaction([STORE_NAME], 'readonly')
        const objectStore = transaction.objectStore(STORE_NAME)
        const index = objectStore.index('syncStatus')
        const request = index.getAll('pending')

        request.onsuccess = () => {
            resolve(request.result as LocalAttendanceRecord[])
        }

        request.onerror = () => {
            reject(new Error('Failed to get pending records from IndexedDB'))
        }
    })
}

/**
 * Get pending records from SQLite
 */
async function getAllPendingRecordsSQLite(): Promise<LocalAttendanceRecord[]> {
    const db = await getSQLiteConnection()

    const selectSQL = `
        SELECT * FROM ${TABLE_NAME}
        WHERE syncStatus = 'pending'
        ORDER BY checkInTime ASC;
    `

    const result = await db.query(selectSQL)

    if (!result.values || result.values.length === 0) {
        return []
    }

    return result.values.map(sqliteRowToRecord)
}

/**
 * Get a single record by ID
 * @param id Record ID
 * @returns LocalAttendanceRecord or null if not found
 */
export async function getRecordById(id: string): Promise<LocalAttendanceRecord | null> {
    try {
        if (isNative) {
            return await getRecordByIdSQLite(id)
        } else {
            return await getRecordByIdIndexedDB(id)
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown retrieval error'
        console.error('[LocalAttendanceStorage] Failed to get record by ID:', errorMessage)
        throw new DatabaseOperationError(`Failed to get record by ID: ${errorMessage}`)
    }
}

/**
 * Get record by ID from IndexedDB
 */
async function getRecordByIdIndexedDB(id: string): Promise<LocalAttendanceRecord | null> {
    return new Promise((resolve, reject) => {
        if (!db) {
            reject(new Error('Database not initialized'))
            return
        }

        const transaction = db.transaction([STORE_NAME], 'readonly')
        const objectStore = transaction.objectStore(STORE_NAME)
        const request = objectStore.get(id)

        request.onsuccess = () => {
            resolve((request.result as LocalAttendanceRecord) || null)
        }

        request.onerror = () => {
            reject(new Error('Failed to get record from IndexedDB'))
        }
    })
}

/**
 * Get record by ID from SQLite
 */
async function getRecordByIdSQLite(id: string): Promise<LocalAttendanceRecord | null> {
    const db = await getSQLiteConnection()

    const selectSQL = `
        SELECT * FROM ${TABLE_NAME}
        WHERE id = ?
        LIMIT 1;
    `

    const result = await db.query(selectSQL, [id])

    if (!result.values || result.values.length === 0) {
        return null
    }

    return sqliteRowToRecord(result.values[0])
}

/**
 * Get records for a specific date (both pending and synced)
 * @param workerId Worker ID
 * @param date Date in ISO format (YYYY-MM-DD)
 * @returns Array of LocalAttendanceRecord
 */
export async function getRecordsByDate(workerId: number, date: string): Promise<LocalAttendanceRecord[]> {
    try {
        if (isNative) {
            return await getRecordsByDateSQLite(workerId, date)
        } else {
            return await getRecordsByDateIndexedDB(workerId, date)
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown retrieval error'
        console.error('[LocalAttendanceStorage] Failed to get records by date:', errorMessage)
        throw new DatabaseOperationError(`Failed to get records by date: ${errorMessage}`)
    }
}

/**
 * Get records by date from IndexedDB
 */
async function getRecordsByDateIndexedDB(workerId: number, date: string): Promise<LocalAttendanceRecord[]> {
    return new Promise((resolve, reject) => {
        if (!db) {
            reject(new Error('Database not initialized'))
            return
        }

        const transaction = db.transaction([STORE_NAME], 'readonly')
        const objectStore = transaction.objectStore(STORE_NAME)
        const request = objectStore.getAll()

        request.onsuccess = () => {
            const allRecords = request.result as LocalAttendanceRecord[]
            console.log('[LocalAttendanceStorage] getRecordsByDate - All records:', allRecords.length)
            console.log('[LocalAttendanceStorage] getRecordsByDate - Query:', { workerId, date })

            const filtered = allRecords.filter(record => {
                if (record.workerId !== workerId) return false
                const recordDate = record.checkInTime.split('T')[0]
                const matches = recordDate === date
                return matches
            })

            console.log('[LocalAttendanceStorage] getRecordsByDate - Filtered records:', filtered.length)
            resolve(filtered)
        }

        request.onerror = () => {
            reject(new Error('Failed to get records by date from IndexedDB'))
        }
    })
}

/**
 * Get records by date from SQLite
 */
async function getRecordsByDateSQLite(workerId: number, date: string): Promise<LocalAttendanceRecord[]> {
    const db = await getSQLiteConnection()

    const selectSQL = `
        SELECT * FROM ${TABLE_NAME}
        WHERE workerId = ? AND DATE(checkInTime) = ?
        ORDER BY checkInTime ASC;
    `

    const result = await db.query(selectSQL, [workerId, date])

    if (!result.values || result.values.length === 0) {
        return []
    }

    return result.values.map(sqliteRowToRecord)
}

/**
 * Mark a record as synced
 */
export async function markAsSynced(id: string): Promise<void> {
    try {
        if (isNative) {
            await markAsSyncedSQLite(id)
        } else {
            await markAsSyncedIndexedDB(id)
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown update error'
        console.error('[LocalAttendanceStorage] Failed to mark as synced:', errorMessage)
        throw new DatabaseOperationError(`Failed to mark as synced: ${errorMessage}`)
    }
}

/**
 * Mark record as synced in IndexedDB
 */
async function markAsSyncedIndexedDB(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (!db) {
            reject(new Error('Database not initialized'))
            return
        }

        const transaction = db.transaction([STORE_NAME], 'readwrite')
        const objectStore = transaction.objectStore(STORE_NAME)
        const getRequest = objectStore.get(id)

        getRequest.onsuccess = () => {
            const record = getRequest.result as LocalAttendanceRecord
            if (!record) {
                reject(new Error(`Record with id ${id} not found`))
                return
            }

            record.syncStatus = 'synced'
            record.updatedAt = new Date().toISOString()
            const putRequest = objectStore.put(record)

            putRequest.onsuccess = () => {
                resolve()
            }

            putRequest.onerror = () => {
                reject(new Error('Failed to update record in IndexedDB'))
            }
        }

        getRequest.onerror = () => {
            reject(new Error('Failed to get record from IndexedDB'))
        }
    })
}

/**
 * Mark record as synced in SQLite
 */
async function markAsSyncedSQLite(id: string): Promise<void> {
    const db = await getSQLiteConnection()

    const updateSQL = `
        UPDATE ${TABLE_NAME}
        SET syncStatus = 'synced', updatedAt = ?
        WHERE id = ?;
    `

    await db.run(updateSQL, [new Date().toISOString(), id])
}

/**
 * Delete a record from the database
 */
export async function deleteRecord(id: string): Promise<void> {
    try {
        if (isNative) {
            await deleteRecordSQLite(id)
        } else {
            await deleteRecordIndexedDB(id)
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown delete error'
        console.error('[LocalAttendanceStorage] Failed to delete record:', errorMessage)
        throw new DatabaseOperationError(`Failed to delete record: ${errorMessage}`)
    }
}

/**
 * Delete record from IndexedDB
 */
async function deleteRecordIndexedDB(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (!db) {
            reject(new Error('Database not initialized'))
            return
        }

        const transaction = db.transaction([STORE_NAME], 'readwrite')
        const objectStore = transaction.objectStore(STORE_NAME)
        const request = objectStore.delete(id)

        request.onsuccess = () => {
            resolve()
        }

        request.onerror = () => {
            reject(new Error('Failed to delete record from IndexedDB'))
        }
    })
}

/**
 * Delete record from SQLite
 */
async function deleteRecordSQLite(id: string): Promise<void> {
    const db = await getSQLiteConnection()
    const deleteSQL = `DELETE FROM ${TABLE_NAME} WHERE id = ?;`
    await db.run(deleteSQL, [id])
}

/**
 * Save multiple synced records from API history (bulk operation)
 * Used to populate local database with server history
 */
export async function saveSyncedRecordsFromHistory(records: LocalAttendanceRecord[]): Promise<void> {
    try {
        for (const record of records) {
            await saveRecord(record)
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown save error'
        console.error('[LocalAttendanceStorage] Failed to save synced records:', errorMessage)
        throw new DatabaseOperationError(`Failed to save synced records: ${errorMessage}`)
    }
}

/**
 * Clear all synced records for a specific worker
 * Used before refreshing from API history
 * @param workerId Worker ID
 * @param startDate Optional start date (YYYY-MM-DD) - if provided, only clear records >= this date
 * @param endDate Optional end date (YYYY-MM-DD) - if provided, only clear records <= this date
 */
export async function clearSyncedRecords(workerId: number, startDate?: string, endDate?: string): Promise<void> {
    try {
        if (isNative) {
            await clearSyncedRecordsSQLite(workerId, startDate, endDate)
        } else {
            await clearSyncedRecordsIndexedDB(workerId, startDate, endDate)
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown clear error'
        console.error('[LocalAttendanceStorage] Failed to clear synced records:', errorMessage)
        throw new DatabaseOperationError(`Failed to clear synced records: ${errorMessage}`)
    }
}

/**
 * Clear synced records from IndexedDB
 * @param workerId Worker ID
 * @param startDate Optional start date (YYYY-MM-DD) - if provided, only clear records >= this date
 * @param endDate Optional end date (YYYY-MM-DD) - if provided, only clear records <= this date
 */
async function clearSyncedRecordsIndexedDB(workerId: number, startDate?: string, endDate?: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (!db) {
            reject(new Error('Database not initialized'))
            return
        }

        const transaction = db.transaction([STORE_NAME], 'readwrite')
        const objectStore = transaction.objectStore(STORE_NAME)
        const request = objectStore.getAll()

        request.onsuccess = () => {
            const allRecords = request.result as LocalAttendanceRecord[]
            const deletePromises = allRecords
                .filter(record => {
                    // Must match workerId and be synced
                    if (record.workerId !== workerId || record.syncStatus !== 'synced') {
                        return false
                    }

                    // If date range specified, filter by it
                    if (startDate || endDate) {
                        const recordDate = record.checkInTime.split('T')[0] // Extract YYYY-MM-DD

                        if (startDate && recordDate < startDate) {
                            return false // Record is before start date
                        }

                        if (endDate && recordDate > endDate) {
                            return false // Record is after end date
                        }
                    }

                    return true
                })
                .map(record => {
                    return new Promise<void>((res, rej) => {
                        const deleteRequest = objectStore.delete(record.id)
                        deleteRequest.onsuccess = () => res()
                        deleteRequest.onerror = () => rej(new Error('Failed to delete record'))
                    })
                })

            Promise.all(deletePromises)
                .then(() => resolve())
                .catch(reject)
        }

        request.onerror = () => {
            reject(new Error('Failed to get records from IndexedDB'))
        }
    })
}

/**
 * Clear synced records from SQLite
 * @param workerId Worker ID
 * @param startDate Optional start date (YYYY-MM-DD) - if provided, only clear records >= this date
 * @param endDate Optional end date (YYYY-MM-DD) - if provided, only clear records <= this date
 */
async function clearSyncedRecordsSQLite(workerId: number, startDate?: string, endDate?: string): Promise<void> {
    const db = await getSQLiteConnection()

    let deleteSQL = `DELETE FROM ${TABLE_NAME} WHERE workerId = ? AND syncStatus = 'synced'`
    const params: any[] = [workerId]

    // Add date range filters if provided
    if (startDate) {
        deleteSQL += ` AND DATE(checkInTime) >= ?`
        params.push(startDate)
    }

    if (endDate) {
        deleteSQL += ` AND DATE(checkInTime) <= ?`
        params.push(endDate)
    }

    deleteSQL += ';'

    await db.run(deleteSQL, params)
}

/**
 * Convert SQLite row to LocalAttendanceRecord
 */
function sqliteRowToRecord(row: any): LocalAttendanceRecord {
    return {
        id: row.id,
        workerId: row.workerId,
        checkInTime: row.checkInTime,
        checkOutTime: row.checkOutTime || null,
        location: {
            lat: row.lat,
            lng: row.lng
        },
        qrCode: row.qrCode,
        action: row.action,
        syncStatus: row.syncStatus,
        status: row.status,
        recordId: row.recordId || undefined,
        createdAt: row.createdAt,
        updatedAt: row.updatedAt || undefined,
        errorMessage: row.errorMessage || undefined,
        retryCount: row.retryCount || 0
    }
}

/**
 * Clear all records from the database (for testing)
 */
export async function clearAllRecords(): Promise<void> {
    try {
        if (isNative) {
            await clearAllRecordsSQLite()
        } else {
            await clearAllRecordsIndexedDB()
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown clear error'
        console.error('[LocalAttendanceStorage] Failed to clear all records:', errorMessage)
        throw new DatabaseOperationError(`Failed to clear all records: ${errorMessage}`)
    }
}

/**
 * Clear all records from IndexedDB
 */
async function clearAllRecordsIndexedDB(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (!db) {
            reject(new Error('Database not initialized'))
            return
        }

        const transaction = db.transaction([STORE_NAME], 'readwrite')
        const objectStore = transaction.objectStore(STORE_NAME)
        const request = objectStore.clear()

        request.onsuccess = () => {
            resolve()
        }

        request.onerror = () => {
            reject(new Error('Failed to clear records from IndexedDB'))
        }
    })
}

/**
 * Clear all records from SQLite
 */
async function clearAllRecordsSQLite(): Promise<void> {
    const db = await getSQLiteConnection()
    const deleteSQL = `DELETE FROM ${TABLE_NAME};`
    await db.run(deleteSQL)
}
